import { trainingFormStatus } from "../constants";

const sumCostByStatus = (data, status, costField) =>
  data
    .filter((item) => item.form_status === status)
    .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

const countEmpIdsByStatus = (data, status) => {
  const ids = new Set();
  data.forEach((item) => {
    if (item?.emp_id && item.form_status === status) {
      ids.add(item.emp_id);
    }
  });
  return ids.size;
};

const buildDemographicBudgetInfo = (
  name,
  filteredData,
  costField,
  { totalBudget = 0, totalHeadCount = 0 } = {},
) => {
  let recommendedBudget = 0;
  let approvedBudget = 0;
  let requestedBudget = 0;

  if (costField === "cost") {
    recommendedBudget = sumCostByStatus(
      filteredData,
      trainingFormStatus.RECOMMENDED,
      costField,
    );
    approvedBudget = sumCostByStatus(
      filteredData,
      trainingFormStatus.APPROVED,
      costField,
    );
    requestedBudget = sumCostByStatus(
      filteredData,
      trainingFormStatus.REQUESTED,
      costField,
    );
  } else {
    recommendedBudget = filteredData.reduce(
      (sum, item) => sum + (Number(item[costField]) || 0),
      0,
    );
  }

  return {
    name,
    recommendedBudget,
    approvedBudget,
    requestedBudget,
    usedBudget: recommendedBudget,
    totalBudget,
    totalHeadCount,
    requestedCount: countEmpIdsByStatus(
      filteredData,
      trainingFormStatus.REQUESTED,
    ),
    recommendedCount: countEmpIdsByStatus(
      filteredData,
      trainingFormStatus.RECOMMENDED,
    ),
    approvedCount: countEmpIdsByStatus(
      filteredData,
      trainingFormStatus.APPROVED,
    ),
  };
};

export const getDemographicsBudgetData = (
  configData,
  allocatedData,
  setaFields,
) => {
  const budgetData = [];
  const { costField, genderField, raceField } = setaFields;

  if (!configData || configData.length === 0) {
    const uniqueDemographics = new Set();

    allocatedData.forEach((item) => {
      if (item[raceField] && item[genderField]) {
        uniqueDemographics.add(`${item[raceField]}-${item[genderField]}`);
      }
    });

    uniqueDemographics.forEach((demographicName) => {
      const [race, gender] = demographicName.split("-");
      const filteredData = allocatedData.filter(
        (item) => item[raceField] === race && item[genderField] === gender,
      );
      budgetData.push(
        buildDemographicBudgetInfo(demographicName, filteredData, costField),
      );
    });

    const otherGenderData = allocatedData.filter(
      (item) =>
        !item[genderField] ||
        item[genderField] === null ||
        item[genderField] === undefined ||
        item[genderField] === "",
    );

    if (otherGenderData.length > 0) {
      const otherGenderByRace = {};
      otherGenderData.forEach((item) => {
        if (!otherGenderByRace[item[raceField]]) {
          otherGenderByRace[item[raceField]] = [];
        }
        otherGenderByRace[item[raceField]].push(item);
      });

      Object.keys(otherGenderByRace).forEach((race) => {
        budgetData.push(
          buildDemographicBudgetInfo(
            `${race}-Other`,
            otherGenderByRace[race],
            costField,
          ),
        );
      });
    }

    return budgetData;
  }

  configData.forEach((raceConfig) => {
    const { race, budget, headCount } = raceConfig;
    const genders = Object.keys(budget);

    genders.forEach((gender) => {
      const budgetShare = Number(budget[gender]) || 0;
      const headCountTarget = Number(headCount?.[gender]) || 0;
      const demographicName = `${race}-${gender}`;
      const filteredData = allocatedData.filter(
        (item) =>
          item[raceField]?.toLowerCase() === race?.toLowerCase() &&
          item[genderField]?.toLowerCase() === gender?.toLowerCase(),
      );

      // Skip empty zero-target cells, but keep rows that have live allocation
      // (e.g. Foreigner often has 0% / 0 headcount yet Recommended spend).
      // Dropping those left Plan Optimiser BBEE C at 0 while Training Category
      // Current Plan still counted the spend.
      if (
        budgetShare === 0 &&
        headCountTarget === 0 &&
        filteredData.length === 0
      ) {
        return;
      }

      budgetData.push(
        buildDemographicBudgetInfo(demographicName, filteredData, costField, {
          totalBudget: budget[gender.toLowerCase()] || 0,
          totalHeadCount: headCount[gender.toLowerCase()] || 0,
        }),
      );
    });
  });

  const validGenders = new Set();
  configData.forEach((raceConfig) => {
    Object.keys(raceConfig.budget).forEach((gender) => {
      validGenders.add(gender);
    });
  });

  const otherGenderData = allocatedData.filter(
    (item) =>
      !item[genderField]?.toLowerCase() ||
      item[genderField]?.toLowerCase() === null ||
      item[genderField]?.toLowerCase() === undefined ||
      item[genderField]?.toLowerCase() === "" ||
      !validGenders.has(item[genderField]?.toLowerCase()),
  );

  if (otherGenderData.length > 0) {
    const otherGenderByRace = {};
    otherGenderData.forEach((item) => {
      if (!otherGenderByRace[item[raceField]?.toLowerCase()]) {
        otherGenderByRace[item[raceField]?.toLowerCase()] = [];
      }
      otherGenderByRace[item[raceField]?.toLowerCase()].push(item);
    });

    Object.keys(otherGenderByRace).forEach((race) => {
      budgetData.push(
        buildDemographicBudgetInfo(
          `${race}-Other`,
          otherGenderByRace[race.toLowerCase()],
          costField,
        ),
      );
    });
  }

  return budgetData;
};
